// deps
import React, { ReactElement, useMemo, useCallback } from "react";
import classNames from "classnames";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// components
import PaymentGroupTitle from "../components/Title";
import PaymentGroupContainer from "../components/Container";
import NetBanking from "@payment-ui/components/payment-method/netbanking";
import { NetbankingMoreBanks } from "@payment-ui/components/netbanking-more-banks";

// constants
import { TEXTS } from "@payment-ui/constants/index";

// helpers
import { isEmpty } from "@swiggy-private/payment-core";
import {
    isPaymentMethodSelected,
    generatePaymentMethodId,
    getPaymentMethods,
    getPaymentGroupDisplayName,
    isPaymentGroupEnabled,
    getPaymentGroupDisableMessage,
    getPaymentGroupIconUrl,
} from "@payment-ui/helpers/Payment";

// interfaces
import { INetBankingPaymentGroup } from "@payment-ui/interfaces/NetBanking";

// widgets
import { ArrowSvgIcon } from "@payment-ui/widgets/arrow-svg-icon";

// test-ids
import { PAYMENT_GROUP_TEST_IDS } from "@swiggy-private/payment-test-ids";

import styles from "../PaymentGroup.v4.scss";

export default function NetBankingPaymentGroup(props: INetBankingPaymentGroup): ReactElement | null {
    const { isFrictionPage } = props;
    const paymentMethods = useMemo(() => getPaymentMethods(props), [props]);
    const isGroupEnabled = useMemo(() => isPaymentGroupEnabled(props.paymentData), [props.paymentData]);

    const onClick = useCallback(
        (e: React.MouseEvent): void => {
            e.preventDefault();
            props.paymentActions.moreBanksActions.onClick();
        },
        [props.paymentActions.moreBanksActions],
    );

    const isMoreBanksListEmpty = useMemo(() => isEmpty(props.netBankingList) || props.netBankingList.length === 0, [
        props.netBankingList,
    ]);

    const showMoreBanksTextLink = useMemo(
        () => isGroupEnabled && !props.isPreferredGroup && !props.showMoreBanksList && !isMoreBanksListEmpty,
        [isGroupEnabled, isMoreBanksListEmpty, props.isPreferredGroup, props.showMoreBanksList],
    );

    const showMoreBanksList = useMemo(() => isGroupEnabled && props.showMoreBanksList && !isMoreBanksListEmpty, [
        isGroupEnabled,
        isMoreBanksListEmpty,
        props.showMoreBanksList,
    ]);

    const disableMessage = useMemo(() => getPaymentGroupDisableMessage(props.paymentData), [props.paymentData]);

    if (isEmpty(paymentMethods)) {
        return null;
    }

    return (
        <PaymentGroupContainer isFrictionPage={isFrictionPage}>
            {!props.hideGroupTitle && (
                <PaymentGroupTitle
                    title={getPaymentGroupDisplayName(props.paymentData)}
                    disableMessage={disableMessage}
                    data-testid={PAYMENT_GROUP_TEST_IDS.TITLE.NETBANKING}
                    groupIcon={getPaymentGroupIconUrl(props.paymentData)}
                />
            )}
            {disableMessage && isFrictionPage && (
                <div className={styles.paymentGroupDisableMsgContainer}>{disableMessage}</div>
            )}
            {isFrictionPage && <PaymentGroupTitle title={TEXTS.POPULAR_BANKS} data-testid={"popular-banks"} />}
            <div
                className={classNames(styles.paymentGroupContainer, {
                    [styles.paymentGroupContainerWithSibling]: showMoreBanksTextLink,
                })}
            >
                <div
                    className={classNames({
                        [styles.paymentGroupContainerNetBanking]: true,
                        [styles.paymentGroupContainerNetBankingRow]: !isFrictionPage,
                    })}
                >
                    {paymentMethods.map(paymentMethod => {
                        const id = generatePaymentMethodId(paymentMethod);
                        return (
                            <NetBanking
                                key={paymentMethod.displayName}
                                id={id}
                                methodData={paymentMethod}
                                isPaymentGroupEnabled={isGroupEnabled}
                                paymentActions={props.paymentActions}
                                selected={isPaymentMethodSelected(props.selectedPaymentId, id)}
                                paymentGroupMeta={props.paymentData.meta}
                                paymentAmount={props.paymentAmount}
                                showListView={isFrictionPage}
                            />
                        );
                    })}
                </div>
            </div>

            {showMoreBanksTextLink && (
                <div
                    className={styles.paymentGroupContainerNetBankingMoreBanks}
                    onClick={onClick}
                    data-testid={PAYMENT_GROUP_TEST_IDS.NETBANKING.MORE_BANKS_LINK}
                >
                    <Text
                        variant="V2.body3.neutral.bold"
                        color="primary"
                        className={styles.paymentGroupContainerNetBankingMoreBanksText}
                    >
                        {TEXTS.MORE_BANKS}
                    </Text>
                    <ArrowSvgIcon />
                </div>
            )}

            <div
                className={classNames({
                    [styles.paymentGroup]: true,
                })}
            >
                {showMoreBanksList && !isEmpty(props.netBankingList) && (
                    <PaymentGroupTitle title={TEXTS.ALL_BANKS} data-testid={"all-banks"} />
                )}
                {/* This is required to show all banks list along with net banking group in case of friction layout */}
                {showMoreBanksList && !isEmpty(props.netBankingList) && (
                    <NetbankingMoreBanks
                        paymentInProgress={props.paymentInProgress}
                        paymentGroupData={props.paymentData}
                        netBankingList={props.netBankingList}
                        onPay={props.paymentActions.onPay}
                    />
                )}
            </div>
        </PaymentGroupContainer>
    );
}
